import { useEffect, useState } from 'react';
import { Menu } from 'antd';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CompanyForm } from './TabForm/CompanyForm';
import { PeopleForm } from './TabForm/PeopleForm';
import { BrmForm } from './TabForm/BrmForm';
import { primaryColor, greyColor, appBgColor, primaryFont, wMedium, maxTablet, maxMobile } from '../../../Utils/utilsStyle';
import { DropdownFooter } from '../../common/dropdown/DropdownFooter';
import { actions as searchActions } from '../../../Slice/AdvanceSearchSlice';
import { actions as uiActions } from '../../../Slice/UISlice';
import {
  selectAdvanceSearchPeopleForm,
  selectAdvanceSearchCompanyForm,
  selectAdvanceSearchBrmForm,
  selectAdvanceSearchMetadataLoading,
  selectAdvanceSearchMetadata,
} from '../../../Selector/AdvanceSearchSelector';
import {
  AdvanceSearchRequest,
  PeopleFormData,
  CompanyFormData,
  BuyerGroupFormData
} from '../../../Types/AdvanceSearchResultsTypes';
import { ADVANCE_SEARCH_ROUTE } from '../../../Utils/Constants';

const Wrapper = styled.div`
.ant-dropdown-menu {
  padding: 8px 16px;
  background-color: ${appBgColor};
  border-bottom: 1px solid #DADADA;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: none;
  gap: 12px;
  .ant-dropdown-menu-item:hover {
    background-color: transparent;
  }
  .ant-dropdown-menu-item-selected {
    background-color: transparent;
  }
  @media all and (max-width: ${maxMobile}) {
    gap: 4px;
  }
}
.tab-link {
  padding: 0px 16px;
  border-radius: 8px;
  background: #FFF;
  border: 1px solid #DADADA;
  color: ${greyColor} !important;
  font-family: ${primaryFont};
  font-size: 14px;
  font-weight: ${wMedium};
  letter-spacing: -0.4px;
  height: 28px;
  cursor: pointer;
  &:active, &:hover {
    color: ${primaryColor} !important;
    background: #DEF6E6;
  }
  &.active {
    color: ${primaryColor} !important;
    background: #DEF6E6;
    border-color: ${primaryColor};
  }
  @media all and (max-width: ${maxTablet}) {
    font-size: 12px;
    padding: 0px 12px;
  }
  @media all and (max-width: ${maxMobile}) {
    font-size: 11px;
  }
  .ant-divider {
    margin: 0;
  }
}
.clear-btn {
  margin-left: auto;
  background: none;
  border: none;
  padding: 0;
  color: ${primaryColor};
  font-family: ${primaryFont};
  font-size: 14px;
  font-weight: ${wMedium};
  letter-spacing: -0.4px;
  line-height: normal;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @media all and (max-width: ${maxTablet}) {
    font-size: 12px;
  }
  @media all and (max-width: ${maxMobile}) {
    font-size: 11px;
  }
}
`;
const ContentWrapper = styled.div`
  /* min-width: 400px;
  max-width: 500px; */
`;
type AnyFormData = PeopleFormData | CompanyFormData | BuyerGroupFormData;

const advanceSearchTabs = [
  { label: 'People', value: 'people' },
  { label: 'Company', value: 'company' },
  { label: 'Buyer Group', value: 'brm' },
];

const buildRequest = (
  tabIndex: number,
  formData: AnyFormData,
): AdvanceSearchRequest => {
  if (tabIndex === 0) {
    const d = formData as PeopleFormData;
    return {
      searchType: 'person',
      fullName: d.fullName || '',
      designation: d.designation || '',
      currentCompany: d.currentCo || '',
      pastCompany: d.previousCo || '',
      country: d.country || '',
      region: d.region || '',
    };
  }
  if (tabIndex === 1) {
    const d = formData as CompanyFormData;
    return {
      searchType: 'company',
      name: d.name || '',
      industry: d.industry || '',
      revenue: d.revenue || '',
      size: d.size || '',
      country: d.country || '',
      region: d.region || '',
    };
  }

  const d = formData as BuyerGroupFormData;
  return {
    searchType: 'brm',
    name: d.name || '',
    member: d.member ? String(d.member) : '',
    size: d.size || '',
    sharedBy: d.sharedBy ? String(d.sharedBy) : '',
  }
};

const AdvanceSearchMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const peopleForm = useSelector(selectAdvanceSearchPeopleForm);
  const companyForm = useSelector(selectAdvanceSearchCompanyForm);
  const brmForm = useSelector(selectAdvanceSearchBrmForm);
  const isLoading = useSelector(selectAdvanceSearchMetadataLoading);
  const advanceSearchMetadata = useSelector(selectAdvanceSearchMetadata);

  const [activeTab, setActiveTab] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [currentFormData, setCurrentFormData] = useState<AnyFormData | null>(null);

  const isFormFilled = (data: AnyFormData | null) => {
    if (!data) return false;
    return Object.values(data).some((val) => {
      if (typeof val === 'number') {
        return val !== 0;
      }
      return String(val).trim() !== '';
    });
  };

  const handleTabClick = (index: number) => {
    setActiveTab(index);
  };

  const handleFormDataChange = (data: AnyFormData) => {
    setCurrentFormData(data);
    setIsFormValid(isFormFilled(data));
  };

  useEffect(() => {
    const data = activeTab === 0
      ? peopleForm
      : activeTab === 1
        ? companyForm
        : brmForm;
    setCurrentFormData(data);
    setIsFormValid(isFormFilled(data));
  }, [activeTab, brmForm, companyForm, peopleForm]);

  const renderForm = () => {
    switch (activeTab) {
      case 0:
        return (
          <PeopleForm
            onValidityChange={setIsFormValid}
            onFormDataChange={handleFormDataChange}
            isLoading={isLoading}
          />
        );
      case 1:
        return (
          <CompanyForm
            onValidityChange={setIsFormValid}
            onFormDataChange={handleFormDataChange}
            isLoading={isLoading}
          />
        );
      case 2:
        return (
          <BrmForm
            onValidityChange={setIsFormValid}
            onFormDataChange={handleFormDataChange}
            isLoading={isLoading}
            brmSizes={advanceSearchMetadata?.brmSizes ?? []}
          />
        );
      default:
        return (
          <PeopleForm
            onValidityChange={setIsFormValid}
            onFormDataChange={handleFormDataChange}
            isLoading={isLoading}
          />
        );
    }
  };

  const handleClearAll = () => {
    dispatch(searchActions.clearAllForms());
  };

  const handleApply = () => {
    if (!isFormValid || !currentFormData) return;
    const request = buildRequest(activeTab, currentFormData);
    dispatch(searchActions.triggerAdvanceSearch(request));
    dispatch(uiActions.isAdvanceSearchOpen(false));
    dispatch(uiActions.isSearchOpen(false));
    const params = new URLSearchParams();
    Object.entries(request).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params.set(key, String(value));
      }
    });
    navigate(`${ADVANCE_SEARCH_ROUTE}?${params.toString()}`);
  };

  return (
    <Wrapper className='advance-search-menu-wrapper' onClick={(e) => e.stopPropagation()}>
      <Menu selectedKeys={[String(activeTab)]} className='d-flex'>
        {advanceSearchTabs.map((tab, index) => (
          <div
            key={index}
            className={`tab-link d-flex align-items-center ${activeTab === index ? 'active' : ''}`}
            onClick={() => handleTabClick(index)}
          >
            {tab.label}
          </div>
        ))}
        <button type='button' disabled={!isFormValid} className='clear-btn' onClick={handleClearAll}>Clear All</button>
      </Menu>
      <ContentWrapper>
        {renderForm()}
        <DropdownFooter title='Apply' disabled={!isFormValid} onApply={handleApply} isInfoSection={true} infoMsg="Please fill in at least one field to apply filters." />
      </ContentWrapper>
    </Wrapper>
  );
};

export default AdvanceSearchMenu;
